import { CheckboxField, InputField } from '@/components/FormElements';
import PaymentMethod from '@/components/PaymentMethod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import Typography from '@/components/ui/typography';
import { yupResolver } from '@hookform/resolvers/yup';
import { router } from '@inertiajs/react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

export const PaymentSchema = yup.object({
  transaction_no: yup.string().required('Transaction Number is required'),
  no_refund_consent: yup
    .boolean()
    .oneOf(
      [true],
      'Please acknowledge and agree to this policy before proceeding.'
    )
    .default(false),
});

type PaymentFormData = yup.InferType<typeof PaymentSchema> & {
  user_id?: number;
};

const SubscriptionDialog = ({
  user_id,
  open,
  onClose,
}: {
  user_id: number;
  open: boolean;
  onClose: (open: boolean) => void;
}) => {
  const form = useForm<PaymentFormData>({
    resolver: yupResolver(PaymentSchema),
    defaultValues: {
      user_id: user_id,
      transaction_no: '',
      no_refund_consent: false,
    },
  });

  // destructure form state to subscribe to proxy form state
  const { isSubmitting } = form.formState;

  const onValidData = async (data: PaymentFormData) => {
    await new Promise<void>((resolve) =>
      router.post(route('subscription'), data, {
        onError: (errors) => {
          for (const field in errors) {
            const error = errors[field];

            // If the field is in the data object, set the error on the form
            // Otherwise, show the error as a toast
            if (Object.prototype.hasOwnProperty.call(data, field)) {
              form.setError(field as keyof typeof data, {
                type: 'server',
                message: error,
              });
            } else {
              toast.error(error);
            }
          }
        },
        onSuccess: ({ props }) => {
          if (props.status) {
            toast.success(props.status as string);
            form.reset();
          }
        },
        onFinish: () => {
          onClose(false);
          resolve();
        },
      })
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>One Time Registration</DialogTitle>
          <DialogDescription>
            You are one step away from enrolling. Pay the registration fee and
            we will get back to you.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onValidData)}
            className="flex flex-col gap-4"
            noValidate
          >
            <div className="flex flex-col gap-4">
              <Typography.Body>
                Amount to be Paid : ₹ <strong>750</strong>
              </Typography.Body>
              <Typography.Subtle className="w-fit font-semibold px-1 bg-primary-muted text-primary text-base">
                Non-Refundable
              </Typography.Subtle>

              <Typography.Subtle className="font-semibold">
                Scan and Pay
              </Typography.Subtle>
              <PaymentMethod />

              <CheckboxField
                control={form.control}
                name="no_refund_consent"
                label="No Refund Policy"
                description={() =>
                  'By proceeding, you acknowledge and agree that no refunds will be issued for this purchase or transaction. All sales are final.'
                }
              />

              <InputField
                control={form.control}
                name="transaction_no"
                label="Transaction No."
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionDialog;
